import {
    CompressionType,
    ConnectedIepeTransducerSettings,
    EnvelopingSettings,
    HardwareStatusCode,
    IConnectedIepeTransducerSettings,
    IEnvelopingSettings,
    IepeAcquisitionUnitHighPassFilter,
    INodeResponse,
    INodeSettingsOptionsResponse,
    INodeSettingsResponse,
    INodeSettingsUpdateRequest,
    INodeUpdateRequest,
    MonitoringPointTypeToNodeTypes,
    NodeSettingsUpdateRequest,
    NodeTrigger,
    NodeTypeCodes,
    NodeUpdateRequest,
    PiezoCollectionType,
    SamplingFrequencyBase8192,
    SamplingFrequencyBase16000,
    SamplingFrequencyBase96000,
} from '@kcftech/prometheus-client';
import { SettingsDocument } from 'components/node_settings/node_details_context';
import { NodeSettingsState } from 'components/node_settings/nodeSettingsState';
import { sdClient } from 'contexts/SDKClientContext';
import { removeUndefinedProperties } from 'utils/object';
import { RecurseNonSdkClass, toSdkClass } from './sdk_types';

const NODE_TYPE_AXIS_COUNT: Record<NodeTypeCodes, 1 | 2 | 3> = {
    // 3-axis
    [NodeTypeCodes.KcfVibrationV4]: 3,

    // 2-axis
    // V3 and V2
    [NodeTypeCodes.KcfVibration]: 2,
    [NodeTypeCodes.KcfExtendedTemperatureVibration]: 2,
    // Wired Vibration Nodes
    [NodeTypeCodes.KcfHubVibrationV1]: 2,

    // 1-axis
    [NodeTypeCodes.Invalid]: 1,
    [NodeTypeCodes.KcfVibrationV1]: 1,
    [NodeTypeCodes.KcfIepeAcquisitionUnitV1]: 1,
    [NodeTypeCodes.KcfHubIepeAdapterV1]: 1,
    [NodeTypeCodes.KcfHubIepeAdapterProV1]: 1,
    [NodeTypeCodes.KcfHubAnalogAdapterV1]: 1,
    [NodeTypeCodes.KcfHubAnalogAdapterProV1]: 1,
    [NodeTypeCodes.KcfAgnostic]: 1,
    [NodeTypeCodes.KcfFlow]: 1,
    [NodeTypeCodes.KcfPressureVoltage]: 1,
    [NodeTypeCodes.KcfPressureCurrent]: 1,
    [NodeTypeCodes.KcfPower]: 1,
    [NodeTypeCodes.KcfVoltage]: 1,
    [NodeTypeCodes.KcfCurrent]: 1,
    [NodeTypeCodes.KcfAtoDVoltage]: 1,
    [NodeTypeCodes.KcfAtoDCurrent]: 1,
    [NodeTypeCodes.KcfOilQuality]: 1,
    [NodeTypeCodes.Generic]: 1,
    [NodeTypeCodes.KcfHumidity]: 1,
};

/**
 * Valid node setting names.
 */
export type NodeSettingName = keyof Omit<INodeSettingsResponse, 'Node'>;

/**
 * Return true if the value passed is timed, attempting to use PendingValue before CurrentValue.
 * Returns false if triggerValue is undefined, or if triggerValue.PendingValue AND triggerValue.CurrentValue are undefined.
 * @param triggerValue
 */
export function isTimedTrigger(
    triggerValue: NodeSettingValue<NodeTrigger> | undefined,
): boolean {
    if (triggerValue === undefined) {
        return false;
    }

    if (
        triggerValue.PendingValue === undefined &&
        triggerValue.CurrentValue === undefined
    ) {
        return false;
    }

    return triggerValue.PendingValue
        ? triggerValue.PendingValue.toLowerCase() === 'timed'
        : triggerValue.CurrentValue?.toLowerCase() === 'timed';
}

/**
 * Return if the node setting value is pending.
 *
 * @param setting The node setting value.
 */
export function isNodeSettingValuePending(setting?: CurrentPendingValue): boolean {
    return (
        setting?.PendingValue !== undefined &&
        setting.PendingValue !== setting.CurrentValue
    );
}
export type OptionChild<TValue = unknown> = {
    Value: TValue;
    Text: string;
};

/** Look up the human-readable Text for a given Value in an OptionChild list. */
export function getOptionText<TValue>(
    options: OptionChild<TValue>[],
    value: TValue | undefined,
): string | undefined {
    return options.find((o) => o.Value === value)?.Text;
}

export type NodeSettingValue<T> = {
    CurrentValue?: T | undefined;
    PendingValue?: T | undefined;
};
/**
 * Returns the node setting pending value or current value.
 *
 * @param setting The setting.
 */
export function getNodeSettingValue<T>(setting?: NodeSettingValue<T>): T | undefined {
    return setting?.PendingValue ?? setting?.CurrentValue;
}

export type FindNodeSettingOptionsResult<T> = {
    /**
     * The current node setting value option.
     */
    current?: OptionChild<T>;
    /**
     * The pending node setting value option.
     */
    pending?: OptionChild<T>;
    /**
     * The node setting value to display (pending value or current value if no pending value).
     */
    display?: OptionChild<T>;
};

/**
 * Finds the option in the supplied node settings options array with a value matching the supplied value.
 * Value can be a `string` or `number` or `NodeSettingValues` object.
 *
 * @param options The valid options to choose from.
 * @param value The node setting value to look up.
 */
export function findNodeSettingOptions<T>(
    options: OptionChild<T>[] | undefined,
    value: string | number | NodeSettingValue<T> | undefined,
): FindNodeSettingOptionsResult<T> {
    const currentKey = typeof value === 'object' ? value.CurrentValue : value?.toString();
    const pendingKey = typeof value === 'object' ? value.PendingValue : undefined;

    if (options === undefined || (currentKey === undefined && pendingKey === undefined)) {
        return { current: undefined, pending: undefined, display: undefined };
    }
    const current = currentKey
        ? options.find((opt) => opt.Value === currentKey)
        : undefined;
    const pending = pendingKey
        ? options.find((opt) => opt.Value === pendingKey)
        : undefined;
    const display = pending ?? current;

    return { current, pending, display };
}

/**
 * converts a string past to a NodeSettingsValues object, with PendingValue and CurrentValue being equal
 * to prevent spinner from appearing prematurely
 * @param value
 */
export function toNodeSettingsValues<T>(value: T | undefined): NodeSettingValue<T> {
    return {
        PendingValue: value,
        CurrentValue: value,
    };
}

/**
 * Mapping the NodeTypeCode to the corresponding Display for each Node Type
 */
export const formattedNodeTypeCodes: Readonly<Record<NodeTypeCodes, string>> = {
    [NodeTypeCodes.KcfAgnostic]: 'KCF Agnostic',
    [NodeTypeCodes.KcfVibrationV1]: 'KCF Vibration V1',
    [NodeTypeCodes.KcfVibration]: 'KCF Vibration',
    [NodeTypeCodes.KcfVibrationV4]: 'KCF Vibration V4',
    [NodeTypeCodes.Generic]: 'KCF Generic Input',
    [NodeTypeCodes.KcfAtoDCurrent]: 'KCF Current A/D',
    [NodeTypeCodes.KcfAtoDVoltage]: 'KCF Voltage A/D',
    [NodeTypeCodes.KcfCurrent]: 'KCF Current',
    [NodeTypeCodes.KcfExtendedTemperatureVibration]: 'KCF Extended Temperature Vibration',
    [NodeTypeCodes.KcfFlow]: 'KCF Flow',
    [NodeTypeCodes.KcfOilQuality]: 'KCF Oil Quality',
    [NodeTypeCodes.KcfHubVibrationV1]: 'IoT HUB Vibration',
    [NodeTypeCodes.KcfHubAnalogAdapterV1]: 'IoT HUB Analog Adapter',
    [NodeTypeCodes.KcfHumidity]: 'KCF Humidity',
    [NodeTypeCodes.KcfPower]: 'KCF Power',
    [NodeTypeCodes.KcfPressureCurrent]: 'KCF Pressure Current',
    [NodeTypeCodes.KcfPressureVoltage]: 'KCF Pressure Voltage',
    [NodeTypeCodes.KcfVoltage]: 'KCF Voltage',
    [NodeTypeCodes.Invalid]: 'Invalid',
    [NodeTypeCodes.KcfHubAnalogAdapterProV1]: 'KCF Hub Analog Adapter Pro V1',
    [NodeTypeCodes.KcfHubIepeAdapterV1]: 'KCF Hub IEPE Adapter V1',
    [NodeTypeCodes.KcfHubIepeAdapterProV1]: 'KCF Hub IEPE Adapter Pro V1',
    [NodeTypeCodes.KcfIepeAcquisitionUnitV1]: 'KCF Piezo Sensing Unit V1',
};

/**
 * Mapping the HardwareStatusCode to the corresponding Display for each Hardware Status
 */
export const formattedHardwareStatusCodes: Readonly<Record<HardwareStatusCode, string>> =
    {
        [HardwareStatusCode.Active]: 'Active',
        [HardwareStatusCode.Retired]: 'Retired',
        [HardwareStatusCode.RmaReceived]: 'RMA Received',
        [HardwareStatusCode.RmaReturning]: 'RMA Returning',
        [HardwareStatusCode.Spare]: 'Spare',
        [HardwareStatusCode.SubmittedForRma]: 'Submitted for RMA',
    };

/**
 * Gets a list of NodeTypeCodes that contain a searched string
 * @param nameSearch The string to search for in the formattedNodeTypeCodes enum
 * @returns a list of formattedNodeTypeCodes that contain the searched string
 *
 * @example
 * getNodeTypeCodesFromSearch('Invalid') // => ['Invalid']
 * getNodeTypeCodesFromSearch('KCF Vib') // => ['KcfVibration', 'KcfVibrationV1', 'KcfVibrationV4']
 */
export function getNodeTypeCodesFromSearch(nameSearch: string) {
    const cleanedSearchValue = nameSearch.replaceAll(' ', '').toLowerCase();
    return (Object.values(NodeTypeCodes) as string[]).filter((code) =>
        code.toLowerCase().includes(cleanedSearchValue),
    );
}

/**
 * The "low" voltage threshold for V3 sensors, in volts (V).
 */
export const lowVoltageVolts = 2.85;

/**
 * Determines if a node voltage is low, based on the voltage threshold.
 */
export const isVoltageLow = (voltage: number) => voltage < lowVoltageVolts;

/**
 * Returns true if the node is exactly a V3 vibration sensor. Will return false
 * non-vibration sensors or older/newer firmware versions.
 */
const isV3VibrationNode = (node: INodeResponse) =>
    node.NodeTypeCode === NodeTypeCodes.KcfVibration &&
    node.FirmwareVersion?.startsWith('3');

/**
 * Returns true if the node is exactly a V4 vibration sensor.
 */
export const isV4VibrationNode = (node: INodeResponse) =>
    node.NodeTypeCode === NodeTypeCodes.KcfVibrationV4;

const pressureNodeTypes = [
    NodeTypeCodes.KcfPressureCurrent,
    NodeTypeCodes.KcfPressureVoltage,
    NodeTypeCodes.KcfAtoDCurrent,
];

/**
 * Returns true if the node is exactly a premium V3 vibration sensor. Will return false
 * non-vibration sensors, older/newer firmware versions, or a V3 Wireless Vibration Standard sensor.
 */
export const isV3PremiumVibrationNode = (node: INodeResponse) =>
    isV3VibrationNode(node) && !isV3StandardVibrationNode(node.SerialNumber);

/**
 * Returns true if the node is exactly a standard V3 vibration sensor. Most recent addition via
 * {@link https://kcftechnologies.atlassian.net/browse/SDWEB-2949}
 */
export const isV3StandardVibrationNode = (nodeSerialNumber: string) => {
    const n = parseInt(nodeSerialNumber, 16);
    if (Number.isNaN(n)) {
        return false;
    }
    return (
        n === 0x000333b4 ||
        (n >= 0x00037ec6 && n <= 0x00037ef3 && n !== 0x00037ed8) ||
        (n >= 0x0003a2c4 && n <= 0x0003a2f3) ||
        (n >= 0x0003cd59 && n <= 0x0003cd88) ||
        (n >= 0x0003e709 && n <= 0x0003e768) ||
        n === 0x0003e7a1 ||
        (n >= 0x0003ed39 && n <= 0x0003ed68 && n !== 0x0003ed44) ||
        (n >= 0x00044667 && n <= 0x00044696) ||
        (n >= 0x00044967 && n <= 0x00044996) ||
        (n >= 0x000449cb && n <= 0x000449d6) ||
        (n >= 0x0004e165 && n <= 0x0004e16d)
    );
};

export type UpdateIntervalOptionsInfo = Readonly<{
    node: INodeResponse | undefined;
    nodeSettingsOptions: INodeSettingsOptionsResponse | undefined;
}>;

/**
 * Returns the UpdateInterval options, which currently differ between Oil Quality sensors,
 * Standard V3 Vibration sensors, and everything else.
 */
export function validUpdateIntervalOptions({
    node,
    nodeSettingsOptions,
}: UpdateIntervalOptionsInfo) {
    if (node?.NodeTypeCode === NodeTypeCodes.KcfOilQuality) {
        return nodeSettingsOptions?.OilQualityUpdateInterval;
    }
    if (node !== undefined && isV3StandardVibrationNode(node.SerialNumber)) {
        return nodeSettingsOptions?.UpdateInterval.filter(
            (c) => c.Value >= 1000 * 60 * 60, // One hour in milliseconds
        );
    }
    return nodeSettingsOptions?.UpdateInterval;
}

/**
 * Determines if the passed Node Type is a Pressure type,
 * accounting for the various different Node Type enums.
 * If nodeType is undefined, return false.
 * @param nodeType to determine if its a pressure type
 */
export const isPressureNodeType = (
    nodeType: NodeTypeCodes | MonitoringPointTypeToNodeTypes | undefined,
): boolean =>
    (nodeType && pressureNodeTypes.includes(nodeType as NodeTypeCodes)) ?? false;

/**
 * Determines if the passed Node Type is a GIN type,
 * (also known as VIN or CIN; Voltage Input Node, Current Input Node)
 * accounting for the various different Node Type enums.
 * If nodeType is undefined, return false.
 * @param nodeType to determine if its a GIN type
 */
export const isVoltageOrCurrentInputNodeType = (
    nodeType: NodeTypeCodes | undefined,
): boolean => (nodeType && nodeType === NodeTypeCodes.KcfAgnostic) ?? false;

const hubNodeTypes = [
    NodeTypeCodes.KcfHubAnalogAdapterV1,
    NodeTypeCodes.KcfHubAnalogAdapterProV1,
    NodeTypeCodes.KcfHubIepeAdapterV1,
    NodeTypeCodes.KcfHubIepeAdapterProV1,
    NodeTypeCodes.KcfHubVibrationV1,
];

/**
 * Returns true if the node is a hub type node
 * @param nodeType The type of the node
 */
export function isHubNode(nodeType: NodeTypeCodes | undefined): boolean {
    return (nodeType && hubNodeTypes.includes(nodeType)) ?? false;
}

/**
 * Returns true if the node is of type Analog Adapter. Will return false otherwise
 */
export const isAnalogNode = (node: INodeResponse) =>
    node.NodeTypeCode === NodeTypeCodes.KcfHubAnalogAdapterV1 ||
    node.NodeTypeCode === NodeTypeCodes.KcfHubAnalogAdapterProV1;

/**
 * Returns true if the node is an IEPE 2.0 Acquisition Unit, false otherwise
 */
export const isAcquisitionUnit = (node: INodeResponse) =>
    node.NodeTypeCode === NodeTypeCodes.KcfIepeAcquisitionUnitV1;

/**
 * Returns true if the node is of type IEPE Adapter. Will return false otherwise
 */
export const isIepeNode = (node: INodeResponse) =>
    node.NodeTypeCode === NodeTypeCodes.KcfHubIepeAdapterV1 ||
    node.NodeTypeCode === NodeTypeCodes.KcfHubIepeAdapterProV1;

/**
 * Updates a node given its id and the updates to make. Loads the
 * node to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param nodeId The id of the node to update
 * @param updates The fields to update on the node
 * @param includeState If the node state should be returned in the node update response
 */
export async function updateNode(
    nodeId: number,
    updates: Partial<INodeUpdateRequest>,
    includeState = false,
) {
    const { StatusCode, Location, Nickname } = await sdClient.getNode({
        nodeId,
        includeState: false,
    });

    const request = new NodeUpdateRequest({
        StatusCode,
        LocationId: Location.LocationId,
        Nickname,
        ...removeUndefinedProperties(updates),
    });

    return sdClient.updateNode({ nodeId, includeState, body: request });
}

const getCompressionTypeNodeSettingValue = (
    setting: NodeSettingValue<CompressionType> | undefined,
): CompressionType | undefined => {
    const compressionTypeString = getNodeSettingValue(setting);
    return stringToCompressionType(compressionTypeString);
};

export const stringToCompressionType = (
    compressionTypeString: string | undefined,
): CompressionType | undefined => {
    if (compressionTypeString === undefined) {
        return undefined;
    }

    const compressionType = CompressionType[
        compressionTypeString as keyof typeof CompressionType
    ] as CompressionType | undefined;
    if (compressionType === undefined) {
        throw new Error('Provided string was not convertible to a compression type.');
    }

    return compressionType;
};

/**
 * Updates node settings given its id and the updates to make. Loads the
 * node settings to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param nodeId The id of the node to update
 * @param updates The node settings to update
 */
export async function updateNodeSettingsObj(
    nodeId: number,
    updates: Partial<INodeSettingsUpdateRequest>,
) {
    const { request, node } = await createNodeSettingsUpdateRequestFromPartial(
        nodeId,
        updates,
    );
    return updateNodeSettingAsync(node, { ...request, ...updates });
}

export async function createNodeSettingsUpdateRequestFromPartial(
    nodeId: number,
    updates: Partial<INodeSettingsUpdateRequest>,
): Promise<{
    request: INodeSettingsUpdateRequest;
    node: INodeResponse;
}> {
    const { request, node } = await buildBaseSettingUpdateRequest(nodeId);
    const newRequest = { ...request, ...updates };

    cleanUpdateRequestBeforeSend(node, newRequest);
    return { request: newRequest, node };
}

function cleanUpdateRequestBeforeSend(
    node: INodeResponse,
    request: INodeSettingsUpdateRequest,
) {
    /*
     * Somewhere somehow for HUB nodes, Trigger Type is getting set to "".
     * HUB Nodes should always have a trigger type of Timed (if triggering is not enabled) or
     * Timed, A or B (if triggering is enabled)
     *
     * For more clarification, contact <mrigank.doshy@kcftech.com> or <egingrich@kcftech.com>
     */
    request.Trigger =
        isHubNode(node.NodeTypeCode) && !request.Trigger
            ? NodeTrigger.Timed
            : request.Trigger;

    /*
     * We want to explicitly not clear out trigger delay if trigger is of type "Timed"
     * even though the UI does not allow setting a trigger delay if trigger is "Timed"
     * This is because we would like to retain the previous trigger delay value so that
     * users don't need to remember specific numbers.
     * Example scenario:
     * * Trigger: A or B, Trigger Delay: 937ms
     * * A user changes Trigger to Timed when there is some maintainance going on on the machine.
     * * After fixing, when the user changes Trigger back to "A", we would like to help remember that the TriggerDelay was 937ms
     *
     * For more clarification, contact <mrigank.doshy@kcftech.com> or <egingrich@kcftech.com>
     */
    request.TriggerDelay = request.Trigger === 'Timed' ? undefined : request.TriggerDelay;
}

export function nodeSettingsStateToUpdateRequestPartial(
    nodeSettingsState: NodeSettingsState,
    collectNow: boolean,
): Partial<INodeSettingsUpdateRequest> {
    return removeUndefinedProperties<INodeSettingsUpdateRequest>({
        SamplingFrequency: nodeSettingsState.samplingFrequency?.PendingValue,
        UpdateInterval: nodeSettingsState.updateInterval?.PendingValue,
        NumberSamples: nodeSettingsState.numberOfSamples?.PendingValue,
        CompressionType: stringToCompressionType(
            nodeSettingsState.compressionType?.PendingValue,
        ),
        CollectNow: collectNow,
        Trigger: nodeSettingsState.trigger?.PendingValue,
        TriggerDelay: nodeSettingsState.triggerDelay?.PendingValue,
        ConnectedIepeTransducerSettings: nodeSettingsState.connectedIepeTransducers
            ?.map((port) =>
                port === undefined
                    ? undefined
                    : {
                          BiasCurrent: getNodeSettingValue(port.biasCurrent),
                          InputCoupling: getNodeSettingValue(port.inputCoupling),
                          InputRange: getNodeSettingValue(port.inputRange),
                          NumberSamples: getNodeSettingValue(port.numberSamples),
                          SamplingFrequency: getNodeSettingValue(port.samplingFrequency),
                          EnvelopingSettings: port.envelopingSettings
                              ? defaultEnvelopingSettings({
                                    NumberSamples: getNodeSettingValue(
                                        port.envelopingSettings.numberSamples,
                                    ),
                                    SamplingFrequency: getNodeSettingValue(
                                        port.envelopingSettings.samplingFrequency,
                                    ),
                                    Filter: getNodeSettingValue(
                                        port.envelopingSettings.filter,
                                    ),
                                })
                              : undefined,
                      },
            )
            .map((portSettings) =>
                portSettings === undefined
                    ? undefined
                    : new ConnectedIepeTransducerSettings(portSettings),
            ),
    });
}

async function updateNodeSettingAsync(
    node: INodeResponse,
    request: INodeSettingsUpdateRequest,
) {
    cleanUpdateRequestBeforeSend(node, request);
    return sdClient.updateNodeSettings({
        nodeId: node.Id,
        body: new NodeSettingsUpdateRequest(request),
    });
}

/**
 * Updates node settings given its id and the updates to make. Loads the
 * node settings to get all current information and adds the updates. Helps prevent getting
 * out of sync with the api or not passing in fields and accidentally clearing them.
 * @param nodeId The id of the node to update
 * @param updates The node settings to update
 */
export async function updateNodeSettings(
    nodeId: number,
    updates: (updateRequest: INodeSettingsUpdateRequest) => void,
) {
    const { request, node } = await buildBaseSettingUpdateRequest(nodeId);

    updates(request);

    return updateNodeSettingAsync(node, request);
}

export async function updatePiezoNodeSinglePortSettings(
    nodeId: number,
    portIndex: number,
    updates: (updateRequest: ConnectedIepeTransducerSettings) => void,
) {
    const portRequest = await buildBasePiezoSettingUpdateRequest(nodeId, portIndex);

    updates(portRequest ?? new ConnectedIepeTransducerSettings({}));

    return sdClient.updateNodePortSettings({
        nodeId,
        portIndex,
        body: portRequest,
    });
}

/**
 * Removes text in parentheses from value (if it doesn't start with a parenthesis).
 * Examples:
 * * "Test (Details)" => "Test"
 * * "(Test)" => "(Test)"
 */
export const getOptionChildText = (optionChild: OptionChild) => {
    const { Text } = optionChild;
    return Text.startsWith('(') ? Text : Text.split('(')[0].trim();
};

export type GetPendingTextOptions = Readonly<{
    current: OptionChild | undefined;
    pending: OptionChild | undefined;
}>;

/**
 * Builds node setting pending text for pending tooltip.
 */
export const getPendingText = ({ current, pending }: GetPendingTextOptions) => {
    return getPendingTextFromStrings(current?.Text, pending?.Text);
};

export const getPendingTextFromStrings = (
    current: string | undefined,
    pending: string | undefined,
) => {
    if (current && pending && current !== pending) {
        return `Updating from ${current} to ${pending}`;
    }
    if (!current && pending) {
        return `Updating to ${pending}`;
    }
    if (!pending && current) {
        return `Clearing value ${current}`;
    }
    return undefined;
};

export type CurrentPendingValue<T = unknown> = Readonly<{
    CurrentValue?: T;
    PendingValue?: T;
}>;

/**
 * Determines if settings are considered equal.
 */
export const settingsAreEqual = <T>(
    previous: CurrentPendingValue<T> | undefined,
    current: CurrentPendingValue<T> | undefined,
) =>
    current?.CurrentValue === previous?.CurrentValue &&
    current?.PendingValue === previous?.PendingValue;

/**
 * Replaces any decimation factor `1` values with `0`.
 */
export const normalizeDecimationFactor = ({
    CurrentValue,
    PendingValue,
}: NodeSettingValue<string>): NodeSettingValue<string> => ({
    CurrentValue: CurrentValue === '1' ? '0' : CurrentValue,
    PendingValue: PendingValue === '1' ? '0' : PendingValue,
});

/**
 * Returns the unit for the analog adapter.
 */
export const getAnalogAdapterInputTypeUnit = (inputType: string | undefined) => {
    switch (inputType?.toLowerCase()) {
        case 'voltage':
            return 'V';
        case 'current':
            return 'mA';
        default:
            return undefined;
    }
};

export const getSamplingFrequencyAsOption = (
    frequencyValue: SamplingFrequency,
    // Whether to round the value
    round?: boolean,
): OptionChild<number> => {
    // At time of writing, the API guarantees that the enum backing values are number-parsable
    const numberValue = Number(frequencyValue);

    return {
        Value: numberValue,
        Text: `${
            round ? Math.round(numberValue).toString() : frequencyValue.toString()
        } Hz`,
    };
};

export type CollectionIntervalExceedsUpdateIntervalProps = Readonly<{
    samplingFrequency: number | undefined;
    numberOfSamples: number | undefined;
    updateInterval: number | undefined;
}>;

export const collectionIntervalExceedsUpdateInterval = ({
    samplingFrequency,
    numberOfSamples,
    updateInterval,
}: CollectionIntervalExceedsUpdateIntervalProps) => {
    if (
        samplingFrequency === undefined ||
        numberOfSamples === undefined ||
        updateInterval === undefined
    ) {
        return false;
    }

    const collectionDurationMs = (numberOfSamples / samplingFrequency) * 1000;

    if (collectionDurationMs >= updateInterval) {
        return true;
    }
};

/**
 * Triggered Piezo Sensing Units fire at most once every 10 minutes. UpdateInterval is
 * unreliable in triggered mode: the collector posts 0 to the device, and the trigger-settings
 * save flow deliberately omits UpdateInterval. Treat the effective update interval as 10
 * minutes when comparing collection duration against it.
 */
export const TRIGGERED_PIEZO_EFFECTIVE_UPDATE_INTERVAL_MS = 10 * 60 * 1000;

export function isTriggeredPiezoUnit(
    nodeTypeCode: NodeTypeCodes | undefined,
    piezoCollectionType: PiezoCollectionType | undefined,
): boolean {
    return (
        nodeTypeCode === NodeTypeCodes.KcfIepeAcquisitionUnitV1 &&
        piezoCollectionType === PiezoCollectionType.ThresholdTriggering
    );
}

export function getEffectiveUpdateInterval(
    nodeTypeCode: NodeTypeCodes | undefined,
    piezoCollectionType: PiezoCollectionType | undefined,
    updateInterval: number | undefined,
): number | undefined {
    if (isTriggeredPiezoUnit(nodeTypeCode, piezoCollectionType)) {
        return TRIGGERED_PIEZO_EFFECTIVE_UPDATE_INTERVAL_MS;
    }
    return updateInterval;
}

export type SamplingFrequency =
    | SamplingFrequencyBase8192
    | SamplingFrequencyBase16000
    | SamplingFrequencyBase96000;

export type SamplingFrequencyTypes = 'Base8192' | 'Base16000';

export type SamplingFrequencyTypeOptionSet = Readonly<{
    displayName: string;
    values: SamplingFrequency[];
    supportedNodeTypes: Set<NodeTypeCodes>;
}>;

export const nodeTypeCodeToSamplingFrequencyValues = (
    code: NodeTypeCodes,
): SamplingFrequency[] => {
    switch (code) {
        case NodeTypeCodes.KcfPressureVoltage:
        case NodeTypeCodes.KcfVibration:
        case NodeTypeCodes.KcfAtoDVoltage:
        case NodeTypeCodes.KcfAtoDCurrent:
        case NodeTypeCodes.KcfOilQuality:
        case NodeTypeCodes.KcfAgnostic:
        case NodeTypeCodes.KcfExtendedTemperatureVibration:
        case NodeTypeCodes.KcfHubVibrationV1:
        case NodeTypeCodes.KcfHubAnalogAdapterV1:
        case NodeTypeCodes.KcfHubAnalogAdapterProV1:
            return Object.values(SamplingFrequencyBase8192);
        case NodeTypeCodes.KcfHubIepeAdapterV1:
        case NodeTypeCodes.KcfHubIepeAdapterProV1:
            // TODO: Remove filter when 62.5 Hz works from the hardware side
            return Object.values(SamplingFrequencyBase16000).filter(
                (frequency) =>
                    frequency !== SamplingFrequencyBase16000.Hz625 &&
                    frequency !== SamplingFrequencyBase16000.Hz3125,
            );
        case NodeTypeCodes.KcfVibrationV4:
            return Object.values(SamplingFrequencyBase16000);
        case NodeTypeCodes.KcfIepeAcquisitionUnitV1:
            return Object.values(SamplingFrequencyBase96000);
        case NodeTypeCodes.Invalid:
        case NodeTypeCodes.Generic:
        case NodeTypeCodes.KcfCurrent:
        case NodeTypeCodes.KcfFlow:
        case NodeTypeCodes.KcfHumidity:
        case NodeTypeCodes.KcfPower:
        case NodeTypeCodes.KcfPressureCurrent:
        case NodeTypeCodes.KcfVibrationV1:
        case NodeTypeCodes.KcfVoltage:
        default:
            return [];
    }
};

export const samplingFrequencyTypeOptions: Record<
    SamplingFrequencyTypes,
    SamplingFrequencyTypeOptionSet
> = {
    Base8192: {
        displayName: 'Base 8192',
        values: Object.values(SamplingFrequencyBase8192),
        supportedNodeTypes: new Set([
            NodeTypeCodes.KcfPressureVoltage,
            NodeTypeCodes.KcfVibration,
            NodeTypeCodes.KcfAtoDVoltage,
            NodeTypeCodes.KcfAtoDCurrent,
            NodeTypeCodes.KcfOilQuality,
            NodeTypeCodes.KcfAgnostic,
            NodeTypeCodes.KcfExtendedTemperatureVibration,
            NodeTypeCodes.KcfHubVibrationV1,
            NodeTypeCodes.KcfHubAnalogAdapterV1,
            NodeTypeCodes.KcfHubAnalogAdapterProV1,
        ]),
    },
    Base16000: {
        displayName: 'Base 16000',
        values: Object.values(SamplingFrequencyBase16000),
        supportedNodeTypes: new Set([
            NodeTypeCodes.KcfHubIepeAdapterV1,
            NodeTypeCodes.KcfHubIepeAdapterProV1,
            NodeTypeCodes.KcfVibrationV4,
        ]),
    },
};

type UserFacingCompressionType = CompressionType.BitStream | CompressionType.Carl2;

export const compressionTypeToDisplayName: Record<UserFacingCompressionType, string> = {
    [CompressionType.BitStream]: 'Standard (Lossless)',
    [CompressionType.Carl2]: 'High Periodicity (Lossy)',
};

export const nodeTypeCodeToCompressionTypeOptions = (
    code: NodeTypeCodes,
): UserFacingCompressionType[] => {
    switch (code) {
        case NodeTypeCodes.KcfVibration:
        case NodeTypeCodes.KcfExtendedTemperatureVibration:
        case NodeTypeCodes.KcfHubVibrationV1:
            return [CompressionType.Carl2, CompressionType.BitStream];
        case NodeTypeCodes.KcfHubIepeAdapterV1:
        case NodeTypeCodes.KcfHubIepeAdapterProV1:
        case NodeTypeCodes.KcfHubAnalogAdapterV1:
        case NodeTypeCodes.KcfHubAnalogAdapterProV1:
        case NodeTypeCodes.KcfVibrationV4:
            return [CompressionType.BitStream];
        case NodeTypeCodes.Invalid:
        case NodeTypeCodes.Generic:
        case NodeTypeCodes.KcfCurrent:
        case NodeTypeCodes.KcfFlow:
        case NodeTypeCodes.KcfHumidity:
        case NodeTypeCodes.KcfPower:
        case NodeTypeCodes.KcfPressureCurrent:
        case NodeTypeCodes.KcfVibrationV1:
        case NodeTypeCodes.KcfVoltage:
        case NodeTypeCodes.KcfAtoDVoltage:
        case NodeTypeCodes.KcfAtoDCurrent:
        case NodeTypeCodes.KcfOilQuality:
        case NodeTypeCodes.KcfAgnostic:
        case NodeTypeCodes.KcfPressureVoltage:
        default:
            return [];
    }
};

/**
 * Compression Type of "High Periodicity" aka "Carl2" is only allowed when
 * the Number of Samples is 4096
 */
export const allowedNumberOfSamplesForHighPeriodicity = 4096;

/**
 * Determines if the passed Node Setting has a current value of passed 'value'
 * or if it was changed and has a pending value of passed 'value'
 * @param nodeSetting The node setting to check for
 * @param value The value to check against
 * @returns true if the current or pending value is 'value', otherwise false
 */
export const isNodeSettingEqualToValue = <T>(
    nodeSetting: NodeSettingValue<T> | undefined,
    value: T | undefined,
) =>
    (nodeSetting?.CurrentValue === value && nodeSetting?.PendingValue === undefined) ||
    nodeSetting?.PendingValue === value;

/**
 * Returns true if the node has monitoring point assignments. False otherwise.
 */
export const hasMonitoringPointAssignments = (node: INodeResponse) =>
    node.MonitoringPoints.length > 0;

export async function buildBaseSettingUpdateRequest(nodeId: number) {
    const settings = await sdClient.getNodeSettings({ nodeId });
    const node = await sdClient.getNode({ nodeId });

    const request: INodeSettingsUpdateRequest = {
        SamplingFrequency: getNodeSettingValue(settings.SamplingFrequency),
        UpdateInterval: getNodeSettingValue(settings.UpdateInterval),
        NumberSamples: getNodeSettingValue(settings.NumberSamples),
        CompressionType: getCompressionTypeNodeSettingValue(settings.CompressionType),
        SiteSurveyEnabled: getNodeSettingValue(settings.SiteSurveyEnabled),
        CollectNow: getNodeSettingValue(settings.CollectNow),
        Trigger: getNodeSettingValue(settings.Trigger),
        TriggerDelay: getNodeSettingValue(settings.TriggerDelay),
        PiezoCollectionType: getNodeSettingValue(settings.PiezoCollectionType),
        ConnectedIepeTransducerSettings: settings.ConnectedIepeTransducerSettings?.map<
            IConnectedIepeTransducerSettings | undefined
        >((port) => {
            if (port === undefined) {
                return undefined;
            }
            return {
                BiasCurrent: getNodeSettingValue(port.BiasCurrent),
                InputCoupling: getNodeSettingValue(port.InputCoupling),
                InputRange: getNodeSettingValue(port.InputRange),
                NumberSamples: getNodeSettingValue(port.NumberSamples),
                SamplingFrequency: getNodeSettingValue(port.SamplingFrequency),
                EnvelopingSettings: port.EnvelopingSettings
                    ? defaultEnvelopingSettings({
                          NumberSamples: getNodeSettingValue(
                              port.EnvelopingSettings.NumberSamples,
                          ),
                          SamplingFrequency: getNodeSettingValue(
                              port.EnvelopingSettings.SamplingFrequency,
                          ),
                          Filter: getNodeSettingValue(port.EnvelopingSettings.Filter),
                      })
                    : undefined,
                TriggerActive: getNodeSettingValue(port.TriggerActive),
                TriggerUpperThresholdMv: getNodeSettingValue(
                    port.TriggerUpperThresholdMv,
                ),
                TriggerDelay: getNodeSettingValue(port.TriggerDelay),
            };
        }).map((portSettings) =>
            portSettings === undefined
                ? undefined
                : new ConnectedIepeTransducerSettings(portSettings),
        ),
    };
    return { request: new NodeSettingsUpdateRequest(request), node };
}

export async function buildBasePiezoSettingUpdateRequest(
    nodeId: number,
    portIndex: number,
): Promise<ConnectedIepeTransducerSettings | undefined> {
    const settings = await sdClient.getNodeSettings({ nodeId });

    return settings.ConnectedIepeTransducerSettings?.filter((_, idx) => idx === portIndex)
        .map<IConnectedIepeTransducerSettings | undefined>((port) => {
            if (port === undefined) {
                return undefined;
            }
            return {
                BiasCurrent: getNodeSettingValue(port.BiasCurrent),
                InputCoupling: getNodeSettingValue(port.InputCoupling),
                InputRange: getNodeSettingValue(port.InputRange),
                NumberSamples: getNodeSettingValue(port.NumberSamples),
                SamplingFrequency: getNodeSettingValue(port.SamplingFrequency),
                EnvelopingSettings: port.EnvelopingSettings
                    ? defaultEnvelopingSettings({
                          NumberSamples: getNodeSettingValue(
                              port.EnvelopingSettings.NumberSamples,
                          ),
                          SamplingFrequency: getNodeSettingValue(
                              port.EnvelopingSettings.SamplingFrequency,
                          ),
                          Filter: getNodeSettingValue(port.EnvelopingSettings.Filter),
                      })
                    : undefined,
                TriggerActive: getNodeSettingValue(port.TriggerActive),
                TriggerUpperThresholdMv: getNodeSettingValue(
                    port.TriggerUpperThresholdMv,
                ),
                TriggerDelay: getNodeSettingValue(port.TriggerDelay),
            };
        })
        .map((portSettings) =>
            portSettings === undefined
                ? undefined
                : new ConnectedIepeTransducerSettings(portSettings),
        )[0];
}

export const IepeAcquisitionUnitV1DefaultSettings = {
    // This is needed because if 1 enveloping setting is provided, all must be.
    // (And I am not presently certain if the HW _always_ has a settings for
    // this. -- This operates under the assumption they might not. If they _do_,
    // we should just use that setting instead.)
    EnvelopingSettings: new EnvelopingSettings({
        Filter: new IepeAcquisitionUnitHighPassFilter({
            Frequency: 500,
        }),
        NumberSamples: 8000,
        SamplingFrequency: Number(SamplingFrequencyBase96000.Hz12000),
    }),
    // This is a logical setting, so it is OK to default it (no risk of
    // overwriting a HW value)
    iepeTransducerSensitivity: 100,
};

export const defaultEnvelopingSettings = (
    es: Partial<IEnvelopingSettings>,
): EnvelopingSettings => {
    return new EnvelopingSettings({
        ...es,
        Filter:
            es.Filter ?? IepeAcquisitionUnitV1DefaultSettings.EnvelopingSettings.Filter,
        NumberSamples:
            es.NumberSamples ??
            IepeAcquisitionUnitV1DefaultSettings.EnvelopingSettings.NumberSamples,
        SamplingFrequency:
            es.SamplingFrequency ??
            IepeAcquisitionUnitV1DefaultSettings.EnvelopingSettings.SamplingFrequency,
    });
};

type TransducerSettings = RecurseNonSdkClass<IConnectedIepeTransducerSettings>;
/**
 * Trigger fields (TriggerActive, TriggerUpperThresholdMv, TriggerDelay, and any
 * future Trigger* fields) come from `target`, not `source`. The piezo trigger
 * flow owns them; copying across ports breaks the one-trigger-port-per-node
 * invariant.
 */
export function copyNonTriggerPortFields(
    target: TransducerSettings | undefined,
    source: TransducerSettings | undefined,
): ConnectedIepeTransducerSettings {
    const newPort = toSdkClass(ConnectedIepeTransducerSettings, target ?? {});
    newPort.SamplingFrequency = source?.SamplingFrequency;
    newPort.NumberSamples = source?.NumberSamples;
    newPort.BiasCurrent = source?.BiasCurrent;
    newPort.InputRange = source?.InputRange;
    newPort.InputCoupling = source?.InputCoupling;
    // fromJS (not the constructor) so the polymorphic Filter is rebuilt with
    // its discriminator. Templates arrive as plain JSON; the constructor only
    // shallow-assigns and would emit a Filter without FilterType, failing API
    // validation.
    newPort.EnvelopingSettings = source?.EnvelopingSettings
        ? EnvelopingSettings.fromJS(source.EnvelopingSettings)
        : undefined;
    return newPort;
}

export function applyPortDraftToRequest(
    draft: SettingsDocument,
    portReq: ConnectedIepeTransducerSettings,
): void {
    portReq.SamplingFrequency = draft.SamplingFrequency;
    portReq.NumberSamples = draft.NumberSamples;
    portReq.EnvelopingSettings =
        draft.EnvelopingSettings === undefined
            ? undefined
            : EnvelopingSettings.fromJS(draft.EnvelopingSettings);
    portReq.BiasCurrent = draft.BiasCurrent;
    portReq.InputRange = draft.InputRange;
    portReq.InputCoupling = draft.InputCoupling;
    portReq.TriggerDelay = draft.TriggerDelay;
}

export function applyNodeDraftToRequest(
    draft: SettingsDocument,
    nodeReq: INodeSettingsUpdateRequest,
): void {
    nodeReq.SamplingFrequency = draft.SamplingFrequency;
    nodeReq.NumberSamples = draft.NumberSamples;
    nodeReq.UpdateInterval = draft.UpdateInterval;
    nodeReq.SiteSurveyEnabled = draft.SiteSurveyEnabled;
    nodeReq.CollectNow = draft.CollectNow;
    nodeReq.CompressionType = draft.CompressionType;
    nodeReq.Trigger = draft.Trigger;
    nodeReq.TriggerDelay = draft.TriggerDelay;
}

export const nodeTypeHasPorts = (nodeType: NodeTypeCodes) =>
    nodeType === NodeTypeCodes.KcfIepeAcquisitionUnitV1;

export const nodeTypeSupportsFilteredDataStreams = (nodeType: NodeTypeCodes) =>
    nodeType === NodeTypeCodes.KcfIepeAcquisitionUnitV1;

export function getAxisCount(nodeType: NodeTypeCodes): 1 | 2 | 3 {
    return NODE_TYPE_AXIS_COUNT[nodeType];
}

export type OrientationAxis = 'X' | 'Y' | 'Z';

export type OrientationFieldConfig = Readonly<{
    axes: readonly OrientationAxis[];
    isSingleAxis: boolean;
}>;

function getAxesForCount(axisCount: number): OrientationAxis[] {
    if (axisCount === 0) {
        return [];
    }
    if (axisCount === 1) {
        return ['Y'];
    }
    if (axisCount === 2) {
        return ['X', 'Y'];
    }
    if (axisCount === 3) {
        return ['X', 'Y', 'Z'];
    }
    throw new Error(`Unsupported axis count: ${axisCount}`);
}

export function getOrientationFieldConfig(
    nodeType: NodeTypeCodes,
): OrientationFieldConfig {
    const axisCount = getAxisCount(nodeType);
    const axes = getAxesForCount(axisCount);
    return {
        axes,
        isSingleAxis: axisCount === 1,
    };
}

export type NodeSettingsForOrientation = Readonly<{
    XAxisEnabled?: NodeSettingValue<boolean>;
    YAxisEnabled?: NodeSettingValue<boolean>;
    ZAxisEnabled?: NodeSettingValue<boolean>;
}>;

export function isOrientationAxisDisabled(
    axis: OrientationAxis,
    nodeType: NodeTypeCodes,
    nodeSettings: NodeSettingsForOrientation | undefined,
): boolean {
    if (nodeType !== NodeTypeCodes.KcfVibrationV4) {
        return false;
    }

    const enabledSetting = nodeSettings?.[`${axis}AxisEnabled`];

    return getNodeSettingValue(enabledSetting) === false;
}

const inactiveNodeStatusCodes = [
    HardwareStatusCode.RmaReturning,
    HardwareStatusCode.RmaReceived,
    HardwareStatusCode.Retired,
];

export const activeNodeStatusCodes = [
    HardwareStatusCode.Active,
    HardwareStatusCode.Spare,
    HardwareStatusCode.SubmittedForRma,
];

export const isNodeInactive = (status: HardwareStatusCode) =>
    inactiveNodeStatusCodes.includes(status);

export function getEnabledAxes(axes: {
    x: boolean | undefined;
    y: boolean | undefined;
    z: boolean | undefined;
}): ('X' | 'Y' | 'Z')[] {
    const enabledAxes: ('X' | 'Y' | 'Z')[] = [];

    if (axes.x) {
        enabledAxes.push('X');
    }
    if (axes.y) {
        enabledAxes.push('Y');
    }
    if (axes.z) {
        enabledAxes.push('Z');
    }
    return enabledAxes;
}
